import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * DiscrepancyBadge - Shows a colored badge when reported value differs from WTF actual.
 * Green = exact match, Yellow = within threshold, Red = significant gap.
 *
 * @param {number} reported - Value reported by TL/Manager in pulse
 * @param {number} wtfActual - Value from WTF API
 * @param {number} threshold - Percentage threshold for "close" match (default 0.1 = 10%)
 * @param {string} size - Badge size: "sm" or "md" (default "sm")
 */
export function DiscrepancyBadge({ reported, wtfActual, threshold = 0.1, size = "sm" }) {
  // Don't render if either value is missing
  if (reported === null || reported === undefined || wtfActual === null || wtfActual === undefined) {
    return null;
  }

  const delta = reported - wtfActual;
  const pctDiff = wtfActual > 0
    ? Math.abs(delta / wtfActual)
    : (delta === 0 ? 0 : 1);

  // Determine color and label based on percentage difference
  let bgColor, textColor, label;

  if (pctDiff === 0) {
    // Exact match
    bgColor = 'bg-green-100';
    textColor = 'text-green-800';
    label = 'Match';
  } else if (pctDiff <= threshold) {
    // Within threshold (close)
    bgColor = 'bg-yellow-100';
    textColor = 'text-yellow-800';
    label = delta > 0 ? `+${delta}` : `${delta}`;
  } else {
    // Significant gap
    bgColor = 'bg-red-100';
    textColor = 'text-red-800';
    label = delta > 0 ? `+${delta}` : `${delta}`;
  }

  const paddingClass = size === "md" ? "px-2 py-1" : "px-1.5 py-0.5";
  const textSizeClass = size === "md" ? "text-xs" : "text-[10px]";

  const tooltipText = `Reported: ${reported} | WTF Actual: ${wtfActual} | Difference: ${delta > 0 ? '+' : ''}${delta}`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center ${paddingClass} rounded ${textSizeClass} font-medium ${bgColor} ${textColor} cursor-default`}
          >
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
